import { useCallback, useEffect } from 'react'

import { getIsFamylocateDomain, hasAnalyticsByLocale } from 'helpers'

import 'root-redux/actions/user'
import {
  selectCookieConsentCountries,
  selectGoogleTagManagerId,
  selectLanguage,
} from 'root-redux/selects/common'
import { selectUserCountryCode } from 'root-redux/selects/user'

import { useBulkSelector } from 'hooks/common/useBulkSelector'

import { COOKIE_CONSENT_LANGUAGES, GAConsent } from 'root-constants/common'

import { useCookieConsentAnswer } from '../common/useCookieConsentAnswer'

const GTM_CUSTOM_PATHS = {
  FAMIO: '/gtg-famio/',
  FAMYLOCATE: '/gtg-famylocate/',
}

const isFamylocateDomain = getIsFamylocateDomain()

const useSelectors = () =>
  useBulkSelector({
    userCountryCode: selectUserCountryCode,
    cookieConsentCountries: selectCookieConsentCountries,
    language: selectLanguage,
    googleTagManagerId: selectGoogleTagManagerId,
  })

export const useGoogleTagManager = (): void => {
  const {
    userCountryCode,
    cookieConsentCountries,
    language,
    googleTagManagerId,
  } = useSelectors()

  const {
    cookieConsent,
    hasAdvertisingAndTargetingCookie,
    hasAnalyticsAndPerformanceCookie,
    shouldApplyCookieConsent,
  } = useCookieConsentAnswer()

  const setDefaultConsentOptions = useCallback(() => {
    if (!shouldApplyCookieConsent || hasAnalyticsByLocale(language)) return

    const script = document.createElement('script')
    script.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
    
      gtag('consent', 'default', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'analytics_storage': 'denied'
      });
    `
    document.body.appendChild(script)
  }, [shouldApplyCookieConsent, language])

  const initGTM = useCallback(() => {
    const gtmScriptSrc = isFamylocateDomain
      ? GTM_CUSTOM_PATHS.FAMYLOCATE
      : GTM_CUSTOM_PATHS.FAMIO

    const script = document.createElement('script')
    script.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        '${gtmScriptSrc}?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer', '${googleTagManagerId}');`

    document.body.appendChild(script)
  }, [googleTagManagerId])

  useEffect(() => {
    if (!userCountryCode || !googleTagManagerId) return

    setDefaultConsentOptions()
    initGTM()
  }, [googleTagManagerId, setDefaultConsentOptions, initGTM, userCountryCode])

  useEffect(() => {
    if (
      !shouldApplyCookieConsent ||
      !COOKIE_CONSENT_LANGUAGES.includes(language) ||
      !cookieConsent?.length
    ) {
      return
    }

    !!window.gtag &&
      window.gtag('consent', 'update', {
        ad_user_data: hasAdvertisingAndTargetingCookie
          ? GAConsent.GRANTED
          : GAConsent.DENIED,
        ad_personalization: hasAdvertisingAndTargetingCookie
          ? GAConsent.GRANTED
          : GAConsent.DENIED,
        ad_storage: hasAdvertisingAndTargetingCookie
          ? GAConsent.GRANTED
          : GAConsent.DENIED,
        analytics_storage: hasAnalyticsAndPerformanceCookie
          ? GAConsent.GRANTED
          : GAConsent.DENIED,
        region: cookieConsentCountries,
      })
  }, [
    cookieConsent?.length,
    cookieConsentCountries,
    hasAdvertisingAndTargetingCookie,
    shouldApplyCookieConsent,
    hasAnalyticsAndPerformanceCookie,
    language,
  ])
}
